import { useActivityFormStore } from "PFApp/activities/activity_form/activity_form_store";
import { useCustomFieldLink } from "PFApp/activities/activity_form/hooks/engagement_link/use_custom_field_link";
import { useSubtemplate } from "PFApp/activities/activity_form/hooks/use_subtemplate";
import { useCustomValuePreviewActions } from "PFApp/components/custom_value_preview/use_custom_value_preview";
import { CustomValuesEditField } from "PFApp/components/custom_values_edit_field";
import { Stack } from "PFComponents/containers";
import { allowsNewValues } from "PFCore/helpers/custom_type";
import { activityCustomTypesAccessLevels } from "PFCore/helpers/custom_types";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { AccessLevel, FeatureFlag, Keyword } from "PFTypes";
import { useTranslation } from "react-i18next";

import { ActivitySkillsSettings } from "../../activity_skills_settings";
import { SuggestionsBox } from "../../suggestions_box";
import { CustomFieldComponentProps } from "./component_prop_types";
import { MatchAttributeField } from "./match_attribute_field";

export const CustomValuesEditFieldWrapper = ({
  property,
  label,
  onChange,
  value,
  error,
  customType,
  connectedValuesIds,
  required,
  disabled
}: CustomFieldComponentProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form.engagementLink" });
  const isEnabled = useIsFeatureEnabled();
  const { data: currentProfile } = useCurrentProfile();
  const template = useActivityFormStore((state) => state.template);
  const activityCreator = useActivityFormStore((state) => state.activityCreator);
  const qaIdPrefix = useActivityFormStore((state) => state.qaIdPrefix);
  const subtemplate = useSubtemplate();

  const { hidePreview } = useCustomValuePreviewActions();

  const activityCanCreateValues = isEnabled(FeatureFlag.ActivityCanCreateValues);
  const developmentalSkillsEnabled = localStorage.getItem("developmental_skills_enabled") === "true";
  const isSkills = customType.name === "skills";
  const showDevelopmentalToggle = developmentalSkillsEnabled && isSkills;

  const { isLinked, canLink, restore } = useCustomFieldLink({
    template,
    subtemplate,
    activityCreator,
    customType,
    value
  });

  const displaySuggestions =
    !disabled &&
    activityCustomTypesAccessLevels(currentProfile, activityCreator)[customType.id] === AccessLevel.ReadWrite;
  const isMultiple = property.kind === "multiple";
  const tip = isLinked ? t("valueLinked") : "";
  const onRestore = !isLinked && canLink ? restore : undefined;

  const handleUpdateAttributeFromSuggestion = (values: Keyword[]) => {
    onChange(values);
    hidePreview();
  };

  if (property.match === true) {
    return (
      <Stack>
        {showDevelopmentalToggle && <ActivitySkillsSettings />}
        <MatchAttributeField
          customType={customType}
          label={label}
          required={required}
          value={value}
          connectedValuesIds={connectedValuesIds}
          isMultiple={isMultiple}
          activityCreator={activityCreator}
          error={error}
          tip={tip}
          onChange={onChange}
          onRestore={onRestore}
          onClear={() => onChange([])}
          disabled={disabled}
        />
        {displaySuggestions && (
          <SuggestionsBox
            attributes={value}
            onMatchAttributeChange={handleUpdateAttributeFromSuggestion}
            userId={currentProfile.id}
            type={customType.name}
          />
        )}
      </Stack>
    );
  }

  return (
    <Stack>
      {showDevelopmentalToggle && <ActivitySkillsSettings />}
      <CustomValuesEditField
        label={label}
        tip={tip}
        customType={customType}
        connectedValuesIds={connectedValuesIds}
        values={value}
        multi={isMultiple}
        qaIdPrefix={qaIdPrefix}
        adminPage={false}
        errors={error}
        letClear
        letCreate={activityCanCreateValues && allowsNewValues(customType)}
        handleChange={onChange}
        required={required}
        hasValuesWithExpiryDate={false}
        isActivity
        activityCreator={activityCreator}
        onRestore={onRestore}
        restoreLabel={t("restore")}
        disabled={disabled}
        requiresSearch={isSkills}
      />
    </Stack>
  );
};
